/* eslint-disable no-underscore-dangle */
/* eslint-disable no-plusplus */
/* eslint-disable import/no-duplicates */
import React, { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { DashboardItem, DashboardList, DashboardFilter } from '../../services/types/Dashboard';
import GanttRow from './GanttRow';
import { SpinnerArea } from '../../shared/components';
import EmptyTableMessage from '../../shared/components/EmptyTableMessage';
import useQuery from '../../shared/hooks/useQuery';

interface Props {
  data: DashboardList;
  filter: DashboardFilter;
  loading: boolean;
}

const GanttView: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const viewRef = useRef<HTMLDivElement>(null);
  const overcomeRef = useRef<HTMLDivElement>(null);
  const overdueRef = useRef<HTMLDivElement>(null);
  const { data, filter, loading } = props;
  const { firstPeriodDay, lastPeriodDay } = data;

  const queryParams = useQuery();
  const q = (key: string) => queryParams.get(key);
  const paramTask = q('tasks');

  useEffect(() => {
    if (paramTask && viewRef.current) {
      if (overcomeRef.current && paramTask === 'OVERCOME') {
        viewRef.current.scrollTo({ behavior: 'smooth', top: overcomeRef.current.offsetTop });
      }
      if (overdueRef.current && paramTask === 'OVERDUE') {
        viewRef.current.scrollTo({ behavior: 'smooth', top: 0 });
      }
    }
  });

  const today = new Date();
  const firstDay: Date = new Date(firstPeriodDay);
  const lastDay: Date = new Date(lastPeriodDay);
  const weeks: { first: number; last: number }[] = [];
  const days: { number: number; weekName: string; monthName: string }[] = [];
  let todayPosition = 0;
  (() => {
    if (filter.period === 'MONTH') {
      let firstWeekDay = firstDay.getDate();
      for (const d = new Date(firstDay.getTime()); d <= lastDay; d.setDate(d.getDate() + 1)) {
        days.push({
          number: d.getDate(),
          weekName: format(d, 'EEEEEE', { locale: enUS }),
          monthName: '',
        });

        if (d.getDay() === 6) {
          weeks.push({ first: firstWeekDay, last: d.getDate() });
        }

        if (d.getDay() === 0) {
          firstWeekDay = d.getDate();
        }
        if (d.getMonth() === today.getMonth() && d.getDate() === today.getDate()) {
          todayPosition = d.getDate();
        }
      }
      if (lastDay.getDay() !== 6) {
        weeks.push({ first: firstWeekDay, last: lastDay.getDate() });
      }
    } else {
      let countPosition = 1;
      for (const d = new Date(firstDay.getTime()); d <= lastDay; d.setDate(d.getDate() + 1)) {
        days.push({
          number: d.getDate(),
          weekName: format(d, 'EEE', { locale: enUS }),
          monthName: format(d, 'MMM do', { locale: enUS }),
        });
        if (d.getMonth() === today.getMonth() && d.getDate() === today.getDate()) {
          todayPosition = countPosition;
        }
        countPosition++;
      }
    }
  })();

  const renderTasks = (list: DashboardItem[]) => {
    let countOvercome = 0;
    let countOverdue = 0;
    return list.map((x, i) => {
      if (x.isOverdue) {
        countOverdue += 1;
      }
      if (!x.isOverdue) {
        countOvercome += 1;
      }
      return (
        <Fragment key={`GanttRow${i.toString()}`}>
          {countOverdue === 1 && <div ref={overdueRef} />}
          {countOvercome === 1 && <div ref={overcomeRef} />}
          <GanttRow
            key={`GanttRow${i.toString()}`}
            taskData={x}
            firstDay={firstDay}
            lastDay={lastDay}
            daysQty={days.length}
          />
        </Fragment>
      );
    });
  };

  const renderHeaderDays = () =>
    days.map((x, i) => (
      <div
        key={`headerDays_${i.toString()}`}
        className={`d-flex flex-column${
          todayPosition > 0 && x.number === today.getDate() ? ' current' : ''
        }`}
      >
        <div>{x.monthName}</div>
        <div>{x.weekName}</div>
      </div>
    ));
  const renderHeaderWeeks = () =>
    weeks.map((x, i) => {
      const daysQty = x.last - x.first;
      return (
        <div key={`headerWeeks_${i.toString()}`} style={{ flex: daysQty + 1 }}>
          <span>{daysQty > 0 ? `${x.first} to ${x.last}` : x.first}</span>
        </div>
      );
    });

  const renderDivisors = () => {
    if (filter.period === 'MONTH') {
      return weeks.map((x, i) => {
        const daysQty = x.last - x.first;
        return (
          <div key={`Divisor_${i.toString()}`} className="div-week" style={{ flex: daysQty + 1 }} />
        );
      });
    }
    return days.map((x, i) => (
      <div key={`Divisor_${i.toString()}`} className="div-week" style={{ flex: 1 }} />
    ));
  };

  return (
    <div className="gantt-container">
      <SpinnerArea loading={loading}>
        {data &&
          (data.list && data.list.length > 0 ? (
            <div className="sticky-section sticky-section--outside_scroll" ref={viewRef}>
              <div className="task-header-container sticky sticky--shadow">
                {weeks.length > 0 && <div className="task-header-weeks">{renderHeaderWeeks()}</div>}
                <div className="task-header-days">{renderHeaderDays()}</div>
              </div>
              <div>{renderTasks(data.list)}</div>
              <div className="div-weeks">{renderDivisors()}</div>
              {todayPosition > 0 && data.list.length > 0 && (
                <div className="today-container">
                  <div className="today-passed" style={{ flex: todayPosition - 0.5 }}>
                    <div className="today-badge">
                      <span>{t('today')}</span>
                    </div>
                  </div>
                  <div className="today-line" />
                  <div style={{ flex: days.length - todayPosition + 0.5 }} />
                </div>
              )}
            </div>
          ) : (
            <EmptyTableMessage />
          ))}
      </SpinnerArea>
    </div>
  );
};

export default GanttView;
